import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { translatedMessage } from '../../service/LanguageService';
import useNavProps from '../../hooks/useNavProps';
import GeneralUtils from '../../utilities/GeneralUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RecruitmentService } from '../../service/RecruitmentService';
import RecruitmentPlanToolbar from './RecruitmentPlanToolbar';
import RecruitmentUtils from '../../utilities/RecruitmentUtils';
import { Divider } from 'primereact/divider';
import RecruitmentPlanInstitutionEdit from './RecruitmentPlanInstitutionEdit';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import ValidationErrorModalComponent from '../../components/ValidationErrorModalComponent';
import RecruitmentPlanStorageFolderFileList from '../../components/file/RecruitmentPlanStorageFolderFileList';
import RecruitmentPlanInvalidate from './RecruitmentPlanInvalidate';

const RecruitmentPlanView = () => {
    const [recruitmentPlan, setRecruitmentPlan] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [editInstitutionDialogVisible, setEditInstitutionDialogVisible] = useState(false);
    const [invalidateDialogVisible, setInvalidateDialogVisible] = useState(false);
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);

    const toast = useRef(null);

    const recruitmentService = useMemo(() => new RecruitmentService(), []);

    const { planIdParam } = useParams();

    const { setNavPropsValue } = useNavProps();

    useEffect(() => {
        setIsLoading(true);

        const getRecruitmentPlan = async () => {
            await recruitmentService.getRecruitmentPlan(planIdParam)
                .then(async (_data) => {
                    // console.log("recruitmentPlan", _data);
                    setRecruitmentPlan(_data);

                    setNavPropsValue('recruitment-plan', _data.id, _data.institution.name)

                    setIsLoading(false);
                })
                .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
        };

        getRecruitmentPlan();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recruitmentService, planIdParam]);

    const afterSave = async (savedData) => {
        setRecruitmentPlan(savedData);
        toast?.current?.show({ severity: 'success', summary: translatedMessage('generic.save.success'), contentClassName: "pcn-toast-success" });
    };

    const afterFileUpload = async (savedData) => {
        if (savedData) {
            setRecruitmentPlan(savedData);
        }
    };


    const editPlan = () => {
        confirmDialog({
            message:
                <div>
                    <div className='mb-2 font-bold'>{translatedMessage('recruitment.plan.confirm.editable')}</div>
                    <div className='mb-2'>{translatedMessage('recruitment.plan.confirm.editable.info')}</div>
                </div>,
            header: translatedMessage('recruitment.plan.editable.title'),
            acceptClassName: 'p-button p-button-primary',
            acceptIcon: 'pi pi-check',
            rejectClassName: 'p-button-text',
            rejectIcon: 'pi pi-times',
            className: 'pcn-confirm-dialog icon-warning',
            icon: "pi pi-exclamation-triangle",
            rejectLabel: translatedMessage("generic.cancel"),
            acceptLabel: translatedMessage("generic.edit"),
            accept: () => acceptEdit()
        });
    }

    const acceptEdit = () => {
        recruitmentService.unlockEditRecruitmentPlan(recruitmentPlan).then(
            (response) => {
                setRecruitmentPlan(response.recruitmentPlan)
                toast?.current?.show({ severity: 'success', summary: translatedMessage('recruitment.plan.editable.success'), contentClassName: "pcn-toast-success" });
            },
            (error) => {
                // console.log(error)
                if (error?.hasValidationError) {
                    setValidationErrors(error.errorCodes)
                    setShowValidationErrorDialog(true)
                } else {
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
                }
            }
        );
    }

    const validatePlan = () => {
        confirmDialog({
            message:
                <div>
                    <div className='mb-2 font-bold'>{translatedMessage('recruitment.plan.confirm.validate')}</div>
                    <div className='mb-2'>{translatedMessage('recruitment.plan.confirm.validate.info')}</div>
                </div>,
            header: translatedMessage('recruitment.plan.validate.title'),
            acceptClassName: 'p-button p-button-primary',
            acceptIcon: 'pi pi-file-pdf',
            rejectClassName: 'p-button-text',
            rejectIcon: 'pi pi-times',
            className: 'pcn-confirm-dialog icon-warning',
            icon: "pi pi-exclamation-triangle",
            rejectLabel: translatedMessage("generic.cancel"),
            acceptLabel: translatedMessage("generic.generatePDF"),
            accept: () => acceptValidation()
        });
    }

    const acceptValidation = () => {
        recruitmentService.validateRecruitmentPlan(recruitmentPlan).then(
            (response) => {
                setRecruitmentPlan(response.recruitmentPlan)
                toast?.current?.show({ severity: 'success', summary: translatedMessage('recruitment.plan.validation.success'), contentClassName: "pcn-toast-success" });
            },
            (error) => {
                // console.log(error)
                if (error?.hasValidationError) {
                    setValidationErrors(error.errorCodes)
                    setShowValidationErrorDialog(true)
                } else {
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
                }
            }
        );
    }

    const invalidatePlan = () => {
        setInvalidateDialogVisible(true);
    }

    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <ConfirmDialog />
                <div className="grid h-full">
                    <div className="col-12">
                        <div className="card h-full">
                            <div className='w-full grid pcn-data-view-panel'>
                                <div className='col-12 flex flex-row justify-content-between'>
                                    <h4 className='mb-1 font-weight-normal'>{translatedMessage("recruitment.plan") + " - " + recruitmentPlan?.institution?.name}</h4>
                                    <div className='ml-3'>
                                        <div className={`inline-block status status-recruitmenPlan-${recruitmentPlan?.status?.toLowerCase()}`}>{translatedMessage("RecruitmentPlanStatus." + recruitmentPlan?.status)}</div>
                                        <span className='ml-2'>
                                            {recruitmentPlan.status === "COMPETITION_JOB" && recruitmentPlan?.registrationNumber
                                                ? (translatedMessage("recruitment.plan.registrationNumber") + ": " + recruitmentPlan?.registrationNumber + " / " + GeneralUtils.formatDate(recruitmentPlan?.registrationDate))
                                                : (translatedMessage("recruitment.plan.deadline") + ": " + GeneralUtils.formatDate(recruitmentPlan?.deadline))
                                            }
                                        </span>
                                    </div>
                                </div>

                                <div className='col-12 pt-0'>
                                    <RecruitmentPlanToolbar recruitmentPlan={recruitmentPlan} code="DETAIL" />

                                    <div className='mt-3'>
                                        <Button
                                            className="pcn-button-slim-with-icon p-button-success mr-2"
                                            onClick={validatePlan}
                                            visible={RecruitmentUtils.canValidate(recruitmentPlan)}
                                        >
                                            <div className='flex align-items-center'>
                                                <FontAwesomeIcon icon='fa-solid fa-check' className="mr-1 " />
                                                <span>{translatedMessage('recruitment.plan.validate')}</span>
                                            </div>
                                        </Button>
                                        <Button
                                            className="pcn-button-slim-with-icon p-button-danger mr-2"
                                            onClick={invalidatePlan}
                                            visible={RecruitmentUtils.canInValidate(recruitmentPlan)}
                                        >
                                            <div className='flex align-items-center'>
                                                <FontAwesomeIcon icon='fa-solid fa-circle-xmark' className="mr-1 " />
                                                <span>{translatedMessage('recruitment.plan.invalidate')}</span>
                                            </div>
                                        </Button>
                                        <Button
                                            className="pcn-button-slim-with-icon p-button mr-2"
                                            onClick={editPlan}
                                            visible={RecruitmentUtils.canMoveToDraft(recruitmentPlan)}
                                        >
                                            <div className='flex align-items-center'>
                                                <FontAwesomeIcon icon='fa-solid fa-pencil' className="mr-1 " />
                                                <span>{translatedMessage('recruitment.plan.edit')}</span>
                                            </div>
                                        </Button>
                                        <Button
                                            className="pcn-button-slim-with-icon p-button mr-2"
                                            onClick={() => setEditInstitutionDialogVisible(true)}
                                            disabled={!RecruitmentUtils.canEditInstitution(recruitmentPlan)}
                                        >
                                            <div className='flex align-items-center'>
                                                <FontAwesomeIcon icon='fa-solid fa-pencil' className="mr-1 " />
                                                <span>{translatedMessage('institution.edit')}</span>
                                            </div>
                                        </Button>
                                    </div>
                                    <Divider />
                                    <h5 className='mb-1'>{translatedMessage("institution.details")}</h5>
                                </div>

                                <div className='col-12 md:col-6'>
                                    <div className='filed-label'>{translatedMessage('recruitment.plan.responsibleName')}</div>
                                    <div className='filed-value'>{GeneralUtils.dashIfNullForObject(recruitmentPlan?.responsibleName)}</div>
                                </div>
                                <div className='col-12 md:col-6'>
                                    <div className='filed-label'>{translatedMessage('recruitment.plan.responsiblePosition')}</div>
                                    <div className='filed-value'>{GeneralUtils.dashIfNullForObject(recruitmentPlan?.responsiblePosition)}</div>
                                </div>
                                <div className='col-12 md:col-6'>
                                    <div className='filed-label'>{translatedMessage('recruitment.plan.responsiblePhone')}</div>
                                    <div className='filed-value'>{GeneralUtils.dashIfNullForObject(recruitmentPlan?.responsiblePhone)}</div>
                                </div>
                                <div className='col-12 md:col-6'>
                                    <div className='filed-label'>{translatedMessage('recruitment.plan.responsibleEmail')}</div>
                                    <div className='filed-value'>{GeneralUtils.dashIfNullForObject(recruitmentPlan?.responsibleEmail)}</div>
                                </div>
                                <div className='col-12 md:col-6'>
                                    <div className='filed-label'>{translatedMessage('institution.cif')}</div>
                                    <div className='filed-value'>{GeneralUtils.dashIfNullForObject(recruitmentPlan?.institution?.cif)}</div>
                                </div>

                                <div className='col-12 mt-5'>
                                    <Divider />

                                    <RecruitmentPlanStorageFolderFileList
                                        recruitmentPlan={recruitmentPlan}
                                        showUploadButton={false}                                        
                                        hasDelete={true}
                                        pageTitle={translatedMessage('generic.files')}
                                        afterFileUpload={afterFileUpload}

                                    // pageSubTitle={translatedMessage('application.file.info')}
                                    // pageInfo={translatedMessage('application.profile.file.details')}
                                    // pageInfoAddFiles={translatedMessage('application.profile.file.getFromProfile.info')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {editInstitutionDialogVisible &&
                    <RecruitmentPlanInstitutionEdit
                        value={recruitmentPlan}
                        visible={editInstitutionDialogVisible}
                        afterSave={afterSave}
                        visibleSetter={setEditInstitutionDialogVisible}
                    />
                }

                {invalidateDialogVisible &&
                    <RecruitmentPlanInvalidate
                        value={recruitmentPlan}
                        visible={invalidateDialogVisible}
                        afterSave={afterSave}
                        visibleSetter={setInvalidateDialogVisible}
                    />
                }

                {showValidationErrorDialog &&
                    <ValidationErrorModalComponent
                        visible={showValidationErrorDialog}
                        errors={validationErrors}
                        dialogTitle={translatedMessage("recruitment.plan.validation.error.title")}
                        dialogInfo={translatedMessage("recruitment.plan.validation.error.info")}
                        closeDialog={() => setShowValidationErrorDialog(false)}
                    />
                }
            </>
        );
    }
};

export default RecruitmentPlanView;
